import { useState, useEffect, useCallback } from "react";
import { Play, BookOpen, HelpCircle, MessageSquare, ChevronLeft, CheckCircle2 } from "lucide-react";
import { FlashcardDeck, type FlashcardItem } from "./FlashcardDeck";
import { Quiz, type QuizQuestion } from "./Quiz";
import { DiscussionQuestions, type DiscussionQuestion } from "./DiscussionQuestions";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

interface LearningModule {
  id: string;
  title: string;
  description: string | null;
  deck_embed_url: string | null;
  voiceover_url: string | null;
  flashcards: FlashcardItem[] | null;
  quiz_questions: QuizQuestion[] | null;
  discussion_questions: DiscussionQuestion[] | null;
  completion_criteria: {
    min_flashcards?: number;
    require_quiz?: boolean;
    require_reflection?: boolean;
  } | null;
}

interface ModulePageProps {
  moduleId: string;
  onBack: () => void;
}

type Tab = "video" | "flashcards" | "quiz" | "discussion";

export function ModulePage({ moduleId, onBack }: ModulePageProps) {
  const { user } = useAuth();
  const [mod, setMod] = useState<LearningModule | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>("video");
  const [completed, setCompleted] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // Load module data
  useEffect(() => {
    if (!supabase) return;
    setLoading(true);
    supabase
      .from("tpa_learning_modules")
      .select("*")
      .eq("id", moduleId)
      .single()
      .then(({ data }) => {
        if (data) {
          setMod(data as LearningModule);
          // Pick the first available tab
          if (data.deck_embed_url || data.voiceover_url) setActiveTab("video");
          else if (data.flashcards?.length) setActiveTab("flashcards");
          else if (data.quiz_questions?.length) setActiveTab("quiz");
          else if (data.discussion_questions?.length) setActiveTab("discussion");
        }
        setLoading(false);
      });

    // Load progress
    supabase
      .from("tpa_learning_progress")
      .select("completed, attempts")
      .eq("module_id", moduleId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setCompleted(data.completed);
          setAttempts(data.attempts);
        }
      });
  }, [moduleId]);

  const upsertProgress = useCallback(
    async (updates: Record<string, unknown>) => {
      if (!supabase || !user) return;
      const { data: existing } = await supabase
        .from("tpa_learning_progress")
        .select("id")
        .eq("user_id", user.id)
        .eq("module_id", moduleId)
        .maybeSingle();

      if (existing) {
        await supabase
          .from("tpa_learning_progress")
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq("id", existing.id);
      } else {
        await supabase
          .from("tpa_learning_progress")
          .insert({ user_id: user.id, module_id: moduleId, ...updates });
      }
    },
    [user, moduleId]
  );

  const handleQuizSubmit = useCallback(
    async (responses: { multipleChoice: Record<string, string>; reflection: Record<string, string> }) => {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setCompleted(true);
      await upsertProgress({
        completed: true,
        completed_at: new Date().toISOString(),
        attempts: newAttempts,
        last_attempt_at: new Date().toISOString(),
        quiz_responses: responses,
      });
    },
    [attempts, upsertProgress]
  );

  const handleDiscussionSave = useCallback(
    async (responses: Record<string, string>) => {
      const text = Object.entries(responses)
        .filter(([, v]) => v.trim())
        .map(([k, v]) => `[${k}] ${v}`)
        .join("\n\n");
      await upsertProgress({ reflection_text: text });
    },
    [upsertProgress]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-muted-foreground">Loading module...</p>
      </div>
    );
  }

  if (!mod) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Module not found</p>
        <button onClick={onBack} className="mt-4 text-sm text-[var(--color-acrrm-dark)] hover:underline">
          Back to modules
        </button>
      </div>
    );
  }

  const hasVideo = !!(mod.deck_embed_url || mod.voiceover_url);
  const hasFlashcards = !!(mod.flashcards && mod.flashcards.length > 0);
  const hasQuiz = !!(mod.quiz_questions && mod.quiz_questions.length > 0);
  const hasDiscussion = !!(mod.discussion_questions && mod.discussion_questions.length > 0);

  const tabs: { key: Tab; label: string; icon: typeof Play; available: boolean }[] = [
    { key: "video", label: "Presentation", icon: Play, available: hasVideo },
    { key: "flashcards", label: "Flashcards", icon: BookOpen, available: hasFlashcards },
    { key: "quiz", label: "Quick Quiz", icon: HelpCircle, available: hasQuiz },
    { key: "discussion", label: "Discussion", icon: MessageSquare, available: hasDiscussion },
  ];

  const availableTabs = tabs.filter((t) => t.available);

  return (
    <div className="space-y-6">
      {/* Back + title */}
      <div>
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to modules
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              {mod.title}
            </h2>
            {mod.description && (
              <p className="text-muted-foreground mt-1">{mod.description}</p>
            )}
          </div>
          {completed && (
            <div className="flex items-center gap-1.5 text-green-600 text-sm font-medium flex-shrink-0 mt-1">
              <CheckCircle2 className="h-4 w-4" />
              Completed{attempts > 1 ? ` (${attempts} attempts)` : ""}
            </div>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      {availableTabs.length > 1 && (
        <div className="border-b">
          <nav className="flex gap-1 -mb-px">
            {availableTabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === key
                    ? "border-[var(--color-acrrm-dark)] text-[var(--color-acrrm-dark)]"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab content */}
      <div>
        {activeTab === "video" && hasVideo && (
          <div className="space-y-4">
            {mod.deck_embed_url && (
              <div>
                {mod.deck_embed_url.endsWith(".mp4") || mod.deck_embed_url.includes("/videos/") ? (
                  <video
                    controls
                    className="w-full rounded-xl border bg-black"
                    src={mod.deck_embed_url}
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="aspect-video rounded-xl overflow-hidden border">
                    <iframe
                      src={mod.deck_embed_url}
                      className="w-full h-full"
                      allow="fullscreen; autoplay"
                      title={mod.title}
                    />
                  </div>
                )}
              </div>
            )}
            {mod.voiceover_url && (
              <div className="rounded-xl border bg-card p-4">
                <p className="text-sm text-muted-foreground mb-2">Audio companion</p>
                <audio controls className="w-full" src={mod.voiceover_url} preload="metadata">
                  Your browser does not support the audio tag.
                </audio>
              </div>
            )}
          </div>
        )}

        {activeTab === "flashcards" && hasFlashcards && (
          <FlashcardDeck
            cards={mod.flashcards!}
            title={`${mod.title} - Flashcards`}
            onComplete={(knew, total) => {
              const criteria = mod.completion_criteria;
              if (criteria?.min_flashcards && knew >= criteria.min_flashcards) {
                // Flashcard threshold met - don't mark complete yet if quiz is also required
              }
            }}
          />
        )}

        {activeTab === "quiz" && hasQuiz && (
          <Quiz
            questions={mod.quiz_questions!}
            title={`${mod.title} - Quick Quiz`}
            onSubmit={handleQuizSubmit}
          />
        )}

        {activeTab === "discussion" && hasDiscussion && (
          <DiscussionQuestions
            questions={mod.discussion_questions!}
            title={`${mod.title} - Discussion`}
            onSave={handleDiscussionSave}
          />
        )}
      </div>
    </div>
  );
}
